import { i18n } from '@/translations/i18n'
import { trackScreenView } from '../../helper'

const state = {
  pending: false,
  checkout: null,
  isReady: false,
  activePaymentMethod: null,
  isBillingAddressSame: true,
  isShippingInfoModalVisible: false,
  shippingInfoContent: null,
}

const getters = {
  isPending: ({ pending }) => pending,
}

const actions = {
  async getCheckout({ commit, state }) {
    commit('SET_PENDING', true)
    try {
      const { data } = await this.api.items.getItem('checkout')
      commit('SET_CHECKOUT', data)
      commit('cart/SET_CART', data.cart, { root: true })
      commit('cart/EMIT_CART_UPDATED', data.cart, { root: true })
      commit('SET_ACTIVE_PAYMENT_METHOD', data.cart?.payment)

      if (!state.isReady) {
        commit('SET_IS_READY')
      }
    } catch (err) {
    } finally {
      commit('SET_PENDING', false)
    }
  },
  async setPaymentMethod({ commit, state }, payment) {
    if (state.pending) return
    commit('SET_PENDING', true)
    try {
      await this.api.items.patchItem('checkout.payment', { payment })
      commit('SET_ACTIVE_PAYMENT_METHOD', payment)
    } catch (err) {
    } finally {
      commit('SET_PENDING', false)
    }
  },
  async setShippingMethod({ commit, dispatch, state }, shipping) {
    if (state.pending) return
    commit('SET_PENDING', true)
    try {
      await this.api.items.patchItem('checkout.shipping', { shipping })
      await dispatch('getCheckout')
    } catch (err) {
    } finally {
      commit('SET_PENDING', false)
    }
  },
  async setWrapping({ commit, dispatch, state }, wrapping) {
    if (state.pending) return
    commit('SET_PENDING', true)
    try {
      await this.api.items.patchItem('checkout.wrapping', { wrapping })
      await dispatch('getCheckout')
    } finally {
      commit('SET_PENDING', false)
    }
  },
  async setShippingAddress({ commit, dispatch, state }, { addressId, sameAsInvoice }) {
    if (state.pending) return
    commit('SET_PENDING', true)
    try {
      const { data } = await this.api.items.patchItem('checkout.delivery-address', { address: addressId, same_as_invoice: sameAsInvoice })
      if (data.redirect === '' || data.redirect === null) {
        await dispatch('getCheckout')
      }
      return data
    } catch (error) {
      throw error
    } finally {
      commit('SET_PENDING', false)
    }
  },
  async setInvoiceAddress({ commit, dispatch, state }, id) {
    if (state.pending) return
    commit('SET_PENDING', true)
    try {
      const { data } = await this.api.items.patchItem('checkout.invoice-address', { address: id })
      await dispatch('getCheckout')
      return data
    } finally {
      commit('SET_PENDING', false)
    }
  },
  async getShippingInfo({ commit, state }) {
    if (state.shippingInfoContent) return state.shippingInfoContent
    const { data } = await this.api.items.getItem('pages.services')
    const item = data?.results?.find(r => r.id === 19)
    const html = item?.description || ''
    commit('SET_SHIPPING_INFO_CONTENT', html)
    return html
  },
  async proceedToPayment({ commit }, payload) {
    commit('SET_PENDING', true)
    try {
      const { data } = await this.api.items.createItem('payments.proceed', payload)
      return data
    } finally {
      commit('SET_PENDING', false)
    }
  },
  async validateAddresses({ commit }) {
    commit('SET_PENDING', true)
    try {
      const { data } = await this.api.items.getItem('checkout.validate_addresses')
      if (data?.tracking_data) {
        trackScreenView('CheckoutValidateAddresses', {
          tracking: data?.tracking_data,
          type: 'event',
        })
      }
      return data
    } catch (error) {
      if (error?.status && [400].includes(error.status)) {
        if (error?.data?.tracking_data) {
          trackScreenView('CheckoutValidateAddresses', {
            tracking: error?.data?.tracking_data,
            type: 'event',
          })
        }
        throw error.data
      } else if (!error.name || error.name !== 'CanceledError') {
        throw error?.data || {}
      }
      throw error
    } finally {
      commit('SET_PENDING', false)
    }
  },
}

const mutations = {
  SET_PENDING: (state, payload) => {
    state.pending = payload
  },
  SET_IS_READY: state => {
    state.isReady = true
  },
  SET_CHECKOUT: (state, payload) => {
    state.checkout = payload
  },
  SET_SHIPPING_ADDRESS: (state, payload) => {
    state.checkout.delivery_address = payload
  },
  SET_INVOICE_ADDRESS: (state, payload) => {
    state.checkout.invoice_address = payload
  },
  SET_ACTIVE_PAYMENT_METHOD: (state, payload) => {
    state.activePaymentMethod = payload
    window.checkoutData = window.ceckoutData || {}
    window.checkoutData.paymentMethod = payload
  },
  SET_IS_BILLING_ADDRESS_SAME(state, payload) {
    state.isBillingAddressSame = payload
  },
  CLEAR_ERRORS(state) {
    state.errors = {
      shipping: {},
      billing: {},
    }
  },
  CLEAR_FIELD_ERROR(state, { type, field }) {
    if (state.errors[type]) {
      delete state.errors[type][field]
    }
  },
  SET_SHIPPING_INFO_MODAL_VISIBLE(state, payload) {
    state.isShippingInfoModalVisible = Boolean(payload)
  },
  SET_SHIPPING_INFO_CONTENT(state, payload) {
    state.shippingInfoContent = payload
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
